import React, { useState } from 'react';
import styled from 'styled-components';
import { Container, Heading, Text, Button } from '../styles/components';
import SectionNumber from './SectionNumber';

const SectionWrapper = styled.section`
  padding: ${props => props.theme.spacing.xxl} 0;
  background-color: ${props => props.theme.colors.bone};
  position: relative;
`;

const ContentContainer = styled(Container)`
  display: grid;
  grid-template-columns: 1fr 1.5fr;
  gap: ${props => props.theme.spacing.xxxl};
  align-items: start;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: ${props => props.theme.spacing.xl};
  }
`;

const IntroWrapper = styled.div`
  text-align: left;
  position: sticky;
  top: 120px;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    position: static;
    margin-bottom: ${props => props.theme.spacing.xl};
    text-align: left;
  }
`;

const FAQList = styled.div`
  width: 100%;
`;

const AccordionItem = styled.div`
  width: 100%;
  border-bottom: 1px solid ${props => props.theme.colors.charcoal};
  
  &:first-child {
    border-top: 1px solid ${props => props.theme.colors.charcoal};
  }
`;

const AccordionButton = styled.button`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${props => props.theme.spacing.lg} 0;
  background: none;
  border: none;
  text-align: left;
  cursor: pointer;
  color: ${props => props.theme.colors.charcoal};
  transition: color 0.2s ease;

  &:hover {
    color: ${props => props.theme.colors.primary};
  }
`;

const QuestionText = styled.h3`
  font-family: ${props => props.theme.typography.body.fontFamily}; // Changed to body font as per image style usually
  font-size: ${props => props.theme.typography.sizes.bodyMid}; // Slightly smaller than H5
  font-weight: 600;
  margin: 0;
  padding-right: ${props => props.theme.spacing.md};
`;

const IconWrapper = styled.div`
  font-size: 1.5rem;
  line-height: 1;
  color: ${props => props.theme.colors.charcoal};
  transform: rotate(${props => props.$isOpen ? '180deg' : '0deg'}); // Changed rotation style to flip arrow or similar
  transition: transform 0.3s ease;
  
  /* Using the specific arrow from the image if possible, but standard chevron is fine for now. 
     The previous one was a + that rotated to x. 
     The image shows a chevron pointing down that likely flips up. 
     Let's keep the user's existing icon logic or adapt if needed. 
     The prompt image shows a chevron. I will switch to a chevron. */
`;

const AccordionContent = styled.div`
  max-height: ${props => props.$isOpen ? '1000px' : '0'};
  overflow: hidden;
  transition: max-height 0.3s ease-in-out, opacity 0.3s ease-in-out, padding 0.3s ease;
  opacity: ${props => props.$isOpen ? 1 : 0};
  padding-bottom: ${props => props.$isOpen ? props.theme.spacing.lg : 0};
`;

const AnswerText = styled(Text)`
  margin: 0;
  white-space: pre-wrap;
  color: ${props => props.theme.colors.charcoal};
  line-height: 1.6;
`;

const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const toggleAccordion = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const faqs = [
    {
      question: "How much is it?",
      answer: `Quite rightly, the first question on most people's minds!

My fees typically range from £5,000-15,000 depending on what you need and what you already have.
I can't give a flat fee because every project is different. 

Some founders come to me with solid financial models, market sizing, IP strategy, and competitive analysis - and they just need help turning it into a compelling narrative and deck. That's one scope of work.
Others come with brilliant technology but the reason they're struggling with story is because the underlying strategy and data is hazy.

Typical investments:

£5-7k: You have strong strategic foundations, just need narrative and deck execution
£8-12k: You need some strategic work (market sizing, moat clarification) plus deck build
£12-15k: You need significant strategic development plus full deck rebuild`
    },
    {
      question: "What if we're not ready to fundraise yet?",
      answer: `I don’t mean to be too forward, but I love you and will you marry me. Because this is the time when we can *really* set you up for your raise. We can do a Strategic Review for £2500 and it will save you *weeks* of wasted time. You’ll have time to course correct on strategy, build relationships with investors, test and socialise the investment case - oh, what a time to be alive!

The sweet spot for the deck itself is when you're 4-6 weeks from starting conversations with investors.`
    },
    {
      question: "How quickly can we get started?",
      answer: `It depends on your availability and mine. I'll need to spend 60-90 minutes with each key team member (or we can do a 2-3 hour messaging workshop) to get started.

The wait time is usually 2-3 weeks maximum, but we can discuss timing on our initial call.`
    },
    {
      question: "How much time will you need from me?",
      answer: `I aim to use your time as efficiently as possible:

Week 1: ~5 hours (deep-dive calls, deck review, strategic alignment)
Weeks 2-3: ~3 hours total (narrative review, check-ins)
Week 4: ~2 hours (pitch practice, refinement)

Total: 10-12 hours over 4-5 weeks
Most of the heavy lifting happens in week 1. After that, it's focused check-ins and reviews.`
    },
    {
      question: "What if we don't like the direction you take?",
      answer: `I don’t take a direction. I help steer you to the one that makes most sense. I'm here to wrangle, challenge, and gently soothe your many ideas into a coherent narrative. But what comes out of our work can only arise from your business, your vision, and your strategy.

There are check-ins built into the process as we layer the work. And I put a practice delivery session into the final week to make sure it feels right before you take it to investors.

I want you to feel confident when you pitch, not like you're delivering someone else's story.`
    },
    {
      question: "What if our pitch deck is already designed?",
      answer: `If we can reuse content, we will. But we start with narrative, not slides. Design is often masking other issues. I don’t even touch slides until we're about 65% of the way through the process. I've learned that slides are the most expensive, labor-intensive way to get confused about your story.

If you have existing brand guidelines and templates, we'll absolutely work within them (and often improve them technically too). But I need to make sure the strategy is sound before we worry about aesthetics.

Remember: There's no point in prettifying slides that don't persuade. Our goal is to get the story to sing, then make visuals that harmonize with it.`
    },
    {
      question: "What's included in the price?",
      answer: `Everything you need for a successful fundraise:
✓ Strategic audit and recommendations report
✓ Narrative architecture and messaging framework
✓ Complete investor deck (typically 15-20 slides + appendices)
✓ Speaker notes and delivery guidance
✓ Pitch practice and feedback session
✓ Editable files (PowerPoint as well as any Adobe/source files I create)
`
    },
    {
      question: "Can you guarantee we'll raise money?",
      answer: `No, and anyone who promises that is lying.

The deck is critical, but it's not the only factor. Your technology needs to work, your team needs to be credible, your timing needs to be right, and you need to actually put in the work to have conversations with investors.

What I guarantee is that you'll have the clearest, most strategically sound pitch deck you've ever had—and that it will give you the best possible shot at success.`
    },
    {
      question: "How is this different from a presentation design agency?",
      answer: `We do design as well, but as my nan would have said, "you can't make a silk purse from a sow's ear". (Actually, she'd have said something ruder than that, but that's Irish nans for you.)

There's no point in prettifying slides that don't persuade. Our goal is to get the strategy and story to sing, then make visuals that harmonise beautifully with it.`
    }
  ];

  return (
    <SectionWrapper id="faq">
      <ContentContainer>
        <IntroWrapper>
          <Heading as="h2" $mb="1rem" $align="left">Got questions?</Heading>
          <Text size="large" mb="2rem">
            I've answered a few here, but if you're looking for more detail you can book in for a chat.
          </Text>
          <Button href="#">Book a Strategy call</Button>
        </IntroWrapper>

        <FAQList>
          {faqs.map((faq, index) => (
            <AccordionItem key={index}>
              <AccordionButton
                onClick={() => toggleAccordion(index)}
                aria-expanded={openIndex === index}
              >
                <QuestionText>{faq.question}</QuestionText>
                <IconWrapper $isOpen={openIndex === index}>
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M4 7L10 13L16 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </IconWrapper>
              </AccordionButton>
              <AccordionContent $isOpen={openIndex === index}>
                <AnswerText>{faq.answer}</AnswerText>
              </AccordionContent>
            </AccordionItem>
          ))}
        </FAQList>
      </ContentContainer>
      <SectionNumber number={9} light />
    </SectionWrapper>
  );
};

export default FAQSection;
